import { Injectable } from "@angular/core";
import { Linqset, NODE_SUPERTYPE, getInsights, isOrganisationNode, isSupplyChain } from "../common/linqset-model";
import { Tag, TagGroup, totalModelInsights } from "../common/organisation";
import { GraphProcessingService } from "./graph-processing.service";
import { LabelService } from "./label.service";
import { GroupNodeTag } from "../canvas/model/graph-model";
import { v4 } from "uuid";
import { AIServices } from "../ai/ai-services";
import { AdjacencyTypes, IEnumerable, IGraph, INode, List, Rect } from "@yfiles/yfiles";
import { totalNodeInsights } from "../common/model-processing";

export enum GROUP_TYPE {
    BY_ACTION,
    BY_VALUE,
    BY_TAG,
    SUMMARY,
    BY_AI,
    BY_FUNCTION
}

class Grouping {
    _key:string         // optional key to use for identification
    nodes: List<INode> = new List<INode>
    description: string // optional, creating by AI

    constructor(public name:string, public value?:number, public tag?:Tag) {
    }

    add(node:INode) {
        this.nodes.add(node)
    }
}

@Injectable({
    providedIn: 'root'
})
export class GroupService {
    constructor(private processingService:GraphProcessingService, private labelService:LabelService, private aiService:AIServices) {}

    public async createGroups(type:GROUP_TYPE, graph:IGraph, linqset:Linqset, tagGroup?:TagGroup) {
        switch (type) {
            case GROUP_TYPE.BY_ACTION:
                this.groupNodesByAction(graph, linqset)
                break
            case GROUP_TYPE.BY_AI:
                await this.groupNodesByAI(graph, linqset)
                break
            case GROUP_TYPE.BY_TAG:
                this.groupNodesByTag(graph, linqset, tagGroup)
                break
            case GROUP_TYPE.BY_VALUE:
                this.groupNodesByValue(graph, linqset)
                break
            case GROUP_TYPE.SUMMARY:
                this.groupNodesBySummary(graph, linqset)
                break
            case GROUP_TYPE.BY_FUNCTION:
                this.groupModelsByFunction(graph, linqset)
                break
        }
    }

    private groupModelsByFunction(graph:IGraph, linqset:Linqset) {
        const groups = graph.nodes.toArray().reduce((groups:List<Grouping>, n) => {
            if (n.tag?.model) {
                if (n.tag.model.function) {
                    let group = groups.find(g => g.name === n.tag.model.function)
                    if (!group) {
                        group = new Grouping(n.tag.model.function)
                        groups.push(group)
                    }
                    group.nodes.push(n)
                }
            }else graph.remove(n)

            return groups
        }, new List<Grouping>())
        graph.edges.toArray().forEach(e => graph.remove(e))

        this.renderGroups(graph, groups, linqset)
    }

    private async groupNodesByAI(graph:IGraph, linqset:Linqset) {
        // nothing comes back if the request fails (the error has been shown)
        const aiGroups = await this.aiService.groupModel(linqset)
        if (!Array.isArray(aiGroups)) return

        const groups = new List<Grouping>()
        aiGroups.forEach(g => {
            const group = new Grouping(g.name)
            group.description = g.description

            // action and information node keys; any the model no longer has are skipped
            const nodeKeys:string[] = g.nodeKeys ?? []
            nodeKeys.forEach(key => {
                const iNode = graph.nodes.find(n => n.tag?.node?._key === key)
                if (iNode) {
                    group.add(iNode)
                    this.addPrevious(graph, iNode, group)
                }
            })
            groups.add(group)
        })
        this.renderGroups(graph, groups, linqset)
    }

    private groupNodesByTag(graph:IGraph, linqset:Linqset, tagGroup:TagGroup) {
        // checked first: grouping an organisation model clears its edges
        if (!tagGroup) return

        let groups = new List<Grouping>()
        const processed = new Set<INode>()

        if (linqset.isOrganisationModel()) {
            groups = graph.nodes.toArray().reduce((groups:List<Grouping>, n) => {
                if (n.tag?.model) {
                    // a model's tags come from every tag group: it goes in the group for its first tag in this one
                    const tagKeys:string[] = n.tag.model.tagKeys ?? []
                    const tk = tagKeys.map(key => tagGroup.tags?.find(t => t._key === key)).find(t => t)
                    if (tk) {
                        let group = groups.find(g => g._key === tk._key)
                        if (!group) {
                            group = new Grouping(tk.name, undefined, tk)
                            group._key = tk._key
                            groups.push(group)
                        }
                        group.nodes.push(n)
                    }
                }else graph.remove(n)

                return groups
            }, new List<Grouping>())
            graph.edges.toArray().forEach(e => graph.remove(e))
        }else {
            const lastNodes = graph.nodes.filter(n => n.tag && n.tag.node && getInsights(n.tag.node, linqset)?.isLastNode)
            lastNodes.forEach(n => {
                processed.add(n)
                this.processNextNodesByTag(graph, n, tagGroup, null, groups, processed)
            })
        }
        this.renderGroups(graph, groups, linqset)
    }

    private groupNodesByAction(graph:IGraph, linqset:Linqset) {
        const actionNodes = graph.nodes.filter(n=> n.tag?.node?.superType === NODE_SUPERTYPE.ACTION).toArray()
        this.renderGroups(graph, new List(actionNodes.map(n => this.getNodesForAction(graph, n))), linqset)
    }

    public groupNodesByModel(graph:IGraph, linqset:Linqset, excludeOrgNodes:boolean=false) {
        const groups = new List<Grouping>()

        graph.nodes.filter(n=> n.tag?.node && isSupplyChain(n.tag.node.superType)).forEach(n => {
            let group = groups.find(g => g._key === n.tag.node.modelKey)
            if (!group) {
                const model = linqset.organisation?.models?.find(m => m._key === n.tag.node.modelKey)
                if (model) {
                    group = new Grouping(model.name)
                    group._key = model._key
                    groups.add(group)
                }
            }
            if (group) {
                group.nodes.add(n)
                if (!excludeOrgNodes) {
                    graph.edgesAt(n, AdjacencyTypes.INCOMING).filter(e => e.sourceNode?.tag?.node && isOrganisationNode(e.sourceNode?.tag?.node.superType)).forEach(e => {
                        group.nodes.add(e.sourceNode)
                        if (e.sourceNode.tag.node.superType === NODE_SUPERTYPE.ROLE) {
                            graph.edgesAt(e.sourceNode, AdjacencyTypes.INCOMING).forEach(e2 => {
                                group.nodes.add(e2.sourceNode)
                            })
                        }
                    })
                }
            }
        })
        this.renderGroups(graph, groups, linqset)
    }

    private groupNodesByValue(graph:IGraph, linqset:Linqset) {
        const groups = new List<Grouping>()
        const processed = new Set<INode>()

        this.processingService.getStartingNodes(graph).forEach(n => {
            processed.add(n)
            this.processNextNodesByValue(graph, linqset, n, null, groups, processed)
        })

        this.renderGroups(graph, groups, linqset)
    }

    // navigates graph creating new group if node has more than one input. if only one input includes in group.
    public groupNodesBySummary(graph:IGraph, linqset:Linqset) {
        const groups = new List<Grouping>()
        const processed = new Set<INode>()

        this.processingService.getStartingNodes(graph).forEach(n => {
            processed.add(n)
            this.processNextNodesBySummary(graph, linqset, n, null, groups, processed)
        })

        this.renderGroups(graph, groups, linqset)
    }

    private getNodesForAction(graph:IGraph, actionNode:INode) {
        const group = new Grouping(actionNode.tag.node.name)

        group.add(actionNode)
        this.addPrevious(graph, actionNode, group)
        return group
    }

    private renderGroups(graph:IGraph, groups:IEnumerable<Grouping>, linqset:Linqset) {

        groups.forEach(g => {
            this.renderGroup(graph, g, linqset)
        })
        this.labelService.updateLabelsForGraph(graph, false, linqset, undefined)
    }

    private renderGroup(graph:IGraph, g:Grouping, linqset:Linqset, leaveOpen:boolean=false) {
        try {
            // a node goes in one group: one shared with an earlier group (a role performing two actions, say) is
            // already inside that group's folder, and no longer in this graph. A group left with nothing isn't shown
            const members = g.nodes.filter(n => graph.contains(n) && !graph.getParent(n)?.tag?.group).toArray()
            if (members.length === 0) return

            const nodeTag = new GroupNodeTag(linqset, v4(), g.name, g.description, g.value, g.tag)

            // need to have unique nodes only
            const nodesMap = g.nodes.reduce((m, n) => {
                if (n.tag?.node) {
                    m[n.tag.node._key] = n.tag.node
                    // set first action node. is used to label tags
                    if (!nodeTag.action && n.tag.node.superType===NODE_SUPERTYPE.ACTION && n.tag.node.properties && n.tag.node.properties.tagKeys) nodeTag.action = n.tag.node
                }else if (n.tag?.model) {
                    m[n.tag.model._key] = n.tag.model
                }
                return m
            }, {})

            if (linqset.isOrganisationModel())
                nodeTag.insights = totalModelInsights(Object.values(nodesMap), linqset.workspaceKey, linqset.organisationKey, linqset.nodes)
            else nodeTag.insights = totalNodeInsights(linqset, Object.values(nodesMap), false)

            nodeTag.insights.processingErrors = [] // not relevant for groups

            const firstNode = members[0]
            const folder = graph.createGroupNode({labels:[g.name], tag: {group:nodeTag}, layout: new Rect(firstNode.layout.x, firstNode.layout.y, 100, 75)})

            if (!leaveOpen) graph.foldingView.collapse(folder)

            // the list can hold a node more than once
            new Set(members).forEach(n => graph.setParent(n, folder))
        }catch(err) {
            console.error(err)
        }
    }

    // adds the roles and systems behind a node, and theirs in turn
    private addPrevious(graph:IGraph, node:INode, nodeList:Grouping) {
        graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => (e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.ROLE || e.sourceNode.tag?.node?.superType === NODE_SUPERTYPE.SYSTEM)).forEach(pe => {
            // already added: roles and systems linked in a loop would otherwise recurse forever
            if (nodeList.nodes.includes(pe.sourceNode)) return
            nodeList.add(pe.sourceNode)
            this.addPrevious(graph, pe.sourceNode, nodeList)
        })
    }

    private processNextNodesBySummary(graph:IGraph, linqset:Linqset, node:INode, nodeGroup:Grouping, groups:List<Grouping>, processed:Set<INode>) {
        const allEdges = graph.edgesAt(node)

        const incomingEdges = allEdges.filter(e => e.targetNode === node && e.sourceNode.tag?.node && isSupplyChain(e.sourceNode.tag.node.superType))
        const nextEdges = allEdges.filter(e => e.sourceNode === node && !processed.has(e.targetNode) && e.targetNode.tag?.node && e.targetNode.tag.node.superType !== NODE_SUPERTYPE.OUTCOMES)

        const addNode = nodeGroup !== null // dont add first one

        if (nodeGroup === null || incomingEdges.size > 1) {
            nodeGroup = new Grouping(node.tag.node.name)
            groups.add(nodeGroup)
        }

        if (addNode) {
            nodeGroup.add(node)
            this.addPrevious(graph, node, nodeGroup)
        }

        nextEdges.forEach(e => {
            // an earlier branch may have reached it since the list was made
            if (processed.has(e.targetNode)) return
            processed.add(e.targetNode)
            this.processNextNodesBySummary(graph, linqset, e.targetNode, nodeGroup, groups, processed)
        })
    }

    private processNextNodesByValue(graph:IGraph, linqset:Linqset, node:INode, nodeGroup:Grouping, groups:List<Grouping>, processed:Set<INode>) {
        const nextEdges = graph.edgesAt(node, AdjacencyTypes.OUTGOING).filter(e => !processed.has(e.targetNode) && e.targetNode.tag?.node && e.targetNode.tag.node.superType !== NODE_SUPERTYPE.OUTCOMES)
        const insights = getInsights(node.tag.node, linqset)

        const addNode = nodeGroup !== null // dont add first one

        if (nodeGroup === null || insights.value !== nodeGroup.value) {
            // not the same as existing so replace with new one
            nodeGroup = new Grouping(node.tag.node.name, insights.value)
            groups.add(nodeGroup)
        }

        if (addNode) {
            nodeGroup.add(node)
            this.addPrevious(graph, node, nodeGroup)
        }

        nextEdges.forEach(e => {
            if (processed.has(e.targetNode)) return
            processed.add(e.targetNode)
            this.processNextNodesByValue(graph, linqset, e.targetNode, nodeGroup, groups, processed)
        })
    }

    // works upstream from the last nodes; a run of actions with the same tag, and what feeds them, forms a group
    private processNextNodesByTag(graph:IGraph, node:INode, tagGroup:TagGroup, nodeGroup:Grouping, allGroups:List<Grouping>, processed:Set<INode>) {
        const nextEdges = graph.edgesAt(node, AdjacencyTypes.INCOMING).filter(e => e.sourceNode?.tag?.node && !processed.has(e.sourceNode))

        if (node.tag.node.superType === NODE_SUPERTYPE.ACTION) {
            const tagKey = node.tag.node.properties?.tagKeys?.[tagGroup._key]
            // a tag since removed from the group counts as untagged
            const tag = tagKey && tagGroup.tags?.find(t => t._key === tagKey)
            if (tag) {
                if (nodeGroup === null || tag._key !== nodeGroup.tag?._key) {
                    // not the same as existing so replace with new one
                    nodeGroup = new Grouping(tag.name, undefined, tag)
                    allGroups.add(nodeGroup)
                }
                nodeGroup.add(node)
            }else {
                nodeGroup = null // reset it
            }
        }else if (nodeGroup !== null) nodeGroup.add(node)

        nextEdges.forEach(e => {
            if (processed.has(e.sourceNode)) return
            processed.add(e.sourceNode)
            this.processNextNodesByTag(graph, e.sourceNode, tagGroup, nodeGroup, allGroups, processed)
        })
    }
}
